import { GCInstant as platform } from 'src/lib/gcinstant';
import i18n from 'src/lib/i18n/i18n';
import { isFirstEntry } from 'src/lib/platformUtils';
import {
  openPopupPromise,
  type PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import type { CardShareKind } from 'src/lib/sharing';
import { withLoading } from 'src/lib/utils';
import { track } from 'src/lib/analytics/utils';
import { Feature } from 'src/replicant/analytics';
import {
  cardsCanReceiveCard,
  cardsCanStartTrade,
  cardsCanSendCard,
  cardsGetOwnedCardCount,
  cardsGetTradeableCards,
  cardsHasCardOrPendingReceived,
  cardsIsAvailable,
  cardsIsCardValid,
  cardsIsEnabled,
} from 'src/replicant/cards/cards.getters';
import {
  type CardId,
  cardsInviteRequestGrantedCardCount,
  cardsUnlockLevel,
} from 'src/replicant/cards/cards.ruleset';
import type { CardsTradeFailureReason } from 'src/replicant/cards/cards.types';
import {
  cardsGetAnalyticsContext,
  cardsGetCardAnalyticsProperties,
  cardsGetDeliveryMethod,
  type CardsDeliveryMethod,
  type CardsLandingState,
} from 'src/replicant/cards/cards.analytics';
import { assertNever } from 'src/replicant/utils';
import { duration } from 'src/replicant/utils/duration';
import StateObserver, { now, user } from 'src/StateObserver';
import { CARD_SHARE_CONFIG, CARD_SHARE_KINDS } from './cards.share';
import {
  cardsCollectReceived,
  cardsEnsurePublicProfile,
  cardsPickCardToTrade,
} from './cards.sequence';

/**
 * Untrusted fields accepted from a card-share entry payload.
 */
type CardsShareEntryData = {
  cardId?: unknown;
  cardShareKind?: unknown;
  cardShareMethod?: unknown;
  tradeId?: unknown;
  subFeature?: unknown;
  $subFeature?: unknown;
  webReferrer?: unknown;
  playerID?: unknown;
  cardShareCreatedAt?: number;
};

/**
 * Validated card-share entry used by request, donate, and trade handlers.
 */
type CardsShareEntry = {
  kind: CardShareKind;
  senderId: string;
  cardId: CardId;
  tradeId?: string;
  expired: boolean;
  requestMethod: CardsDeliveryMethod;
};

/**
 * Options used to present a standard card-share response popup.
 */
type CardsShareResponsePopupOptions = {
  message: string;
  origin: PopupDialogOrigin | undefined;
  count?: number;
};

/**
 * Handler signature shared by request, donate, and trade entry flows.
 */
type CardsShareEntryHandler = (
  entryData: CardsShareEntry,
  origin: PopupDialogOrigin | undefined,
) => Promise<boolean>;

/**
 * Maximum age accepted for card-share links before showing an expired response.
 */
const CARD_SHARE_LINK_DURATION = duration({ days: 2 });

/**
 * Donate claim failures mapped to their localized response messages.
 */
const DONATE_SHARE_FAILURE_MESSAGE_KEYS: Record<string, string> = {
  already_have_card: 'cards.responsePopup.messageAlreadyHaveCard',
  receiver_receive_limit_reached:
    'cards.responsePopup.messageDonateReceivedLimit',
  sender_send_limit_reached: 'cards.responsePopup.messageSenderSentLimit',
};

/**
 * Trade preview and creation failures mapped to localized response messages.
 */
const TRADE_SHARE_FAILURE_MESSAGE_KEYS: Partial<
  Record<CardsTradeFailureReason, string>
> = {
  sender_unavailable: 'cards.responsePopup.messageTradeCardUnavailable',
  sender_trade_limit_reached: 'cards.responsePopup.messageTradeSenderLimit',
  sender_send_limit_reached: 'cards.responsePopup.messageSenderSentLimit',
  sender_receive_limit_reached:
    'cards.responsePopup.messageTradeSenderReceivedLimit',
  opener_trade_limit_reached: 'cards.responsePopup.messageTradeLimit',
  counter_card_unavailable:
    'cards.responsePopup.messageTradeCounterUnavailable',
  trade_already_started: 'cards.responsePopup.messageTradeSent',
};

/**
 * Entry handlers keyed by the validated card-share kind.
 */
const CARD_SHARE_ENTRY_HANDLERS: Record<CardShareKind, CardsShareEntryHandler> =
  {
    request: cardsOpenRequestShareFromEntry,
    donate: cardsOpenDonateShareFromEntry,
    trade: cardsOpenTradeShareFromEntry,
  };

/**
 * Read and validate the current platform entry as a card-share link.
 *
 * @param rawEntryData Entry data to validate, defaulting to the platform entry.
 * @returns A normalized entry, or undefined for invalid or self-authored links.
 */
function cardsGetShareEntryData(
  rawEntryData: unknown = platform.entryData,
): CardsShareEntry | undefined {
  // Read the supplied entry payload as the fields used by card sharing.
  const entryData = rawEntryData as CardsShareEntryData | undefined;
  if (!entryData) {
    return undefined;
  }

  // Card shares must declare one of the current explicit share kinds.
  const explicitKind = entryData.cardShareKind;
  if (!CARD_SHARE_KINDS.includes(explicitKind as CardShareKind)) {
    return undefined;
  }
  const kind = explicitKind as CardShareKind;

  // Use the platform player id when URL sharing does not provide a web referrer.
  const cardId = entryData.cardId;
  const senderId = entryData.webReferrer ?? entryData.playerID;
  const hasValidIdentifiers =
    typeof senderId === 'string' && typeof cardId === 'string';
  if (!hasValidIdentifiers) {
    return undefined;
  }

  // Players cannot respond to their own card-share links.
  if (senderId === user().id) {
    return undefined;
  }

  // Ignore links that reference cards outside the current catalog.
  if (!cardsIsCardValid(cardId)) {
    return undefined;
  }

  // Missing timestamps fall back to the epoch for the expiry check.
  const createdAt = entryData?.cardShareCreatedAt ?? 0;
  const expired = now() - createdAt > CARD_SHARE_LINK_DURATION;

  // Return the normalized entry consumed by the flow-specific handlers.
  const requestMethod = cardsGetDeliveryMethod(
    entryData.cardShareMethod,
    entryData.subFeature ?? entryData.$subFeature,
  );
  const tradeId =
    typeof entryData.tradeId === 'string' ? entryData.tradeId : undefined;

  return { kind, senderId, cardId, tradeId, expired, requestMethod };
}

/**
 * Return whether raw entry data is a valid current card-share payload.
 *
 * @param entryData Raw platform entry data from launch or URL parsing.
 * @returns True when the payload can be handled as a card-share entry.
 */
export function cardsIsCardShareEntryData(entryData: unknown): boolean {
  // Use the same validation as the launch and entry-opening flows.
  return !!cardsGetShareEntryData(entryData);
}

/**
 * Return whether the current player can handle a card-share entry.
 *
 * @returns True when Cards Collection V2 is enabled for the player.
 */
function cardsCanOpenShareLink(): boolean {
  // Card-share links are available only to players with the collection feature.
  return cardsIsEnabled(user());
}

/**
 * Return whether launch should reserve a block for the current card-share link.
 *
 * @returns True when the current entry is valid for this player.
 */
export function cardsIsEnteringFromShareLink(): boolean {
  // The launch block only runs for valid card-share payloads.
  if (!cardsGetShareEntryData()) return false;

  // Unassigned players should continue launch without any Cards response.
  return cardsCanOpenShareLink();
}

/**
 * Open the standard informational response popup for a card-share entry.
 *
 * @param entryData Validated card-share entry being handled.
 * @param options Message, origin, and optional card count override.
 * @returns A promise that resolves after the popup closes.
 */
async function cardsOpenShareResponsePopup(
  entryData: CardsShareEntry,
  options: CardsShareResponsePopupOptions,
): Promise<void> {
  // Informational responses show one card unless the flow needs an exact count.
  await openPopupPromise('popupCardResponse', {
    cardState: {
      id: entryData.cardId,
      count: options.count ?? 1,
      new: false,
    },
    targetId: entryData.senderId,
    title: i18n(CARD_SHARE_CONFIG[entryData.kind].responseTitleKey),
    message: options.message,
    actionMode: 'ok',
    origin: options.origin,
  });
}

/**
 * Open the appropriate flow for the current card-share entry payload.
 *
 * @param origin Popup origin forwarded to response UI.
 * @returns True when a card-share entry was handled, otherwise false.
 */
export async function cardsOpenShareLinkFromEntry(
  origin: PopupDialogOrigin | undefined,
): Promise<boolean> {
  // Ignore launch data that cannot be handled as a card-share entry.
  const entryData = cardsGetShareEntryData();
  if (!entryData) {
    return false;
  }

  // Guests cannot use Cards Collection V2 share links.
  if (!cardsCanOpenShareLink()) return false;

  // Expired links show information without invoking request, donate, or trade APIs.
  if (entryData.expired) {
    trackCardShareLanding(entryData);
    await cardsOpenShareResponsePopup(entryData, {
      message: i18n(CARD_SHARE_CONFIG[entryData.kind].expiredMessageKey),
      origin,
    });
    return true;
  }

  // Ensure the user has a public profile before handling the active share.
  const hasPublicProfile = await cardsEnsurePublicProfile(origin);
  if (!hasPublicProfile) {
    return true;
  }

  // Active card landing UI starts only after profile setup succeeds.
  trackCardShareLanding(entryData);

  // Delegate the valid entry to its request, donate, or trade handler.
  const handler = CARD_SHARE_ENTRY_HANDLERS[entryData.kind];
  return handler(entryData, origin);
}

/** Track one card-share landing impression for a validated entry. */
function trackCardShareLanding(entryData: CardsShareEntry): void {
  // Capture the landing state before reading the recipient's collection context.
  const landingState = cardsGetLandingState(entryData);
  const state = user();

  // Emit link attribution with card metadata and current collection progress.
  track('CardAcceptPopupOpen', {
    landingState,
    requestMethod: entryData.requestMethod,
    senderId: entryData.senderId,
    cardId: entryData.cardId,
    isLinkExpired: entryData.expired,
    ...cardsGetCardAnalyticsProperties(entryData.cardId),
    ...cardsGetAnalyticsContext(state),
  });
}

/**
 * Preview and open the response flow for a request share link.
 *
 * @param entryData Validated request-share entry.
 * @param origin Popup origin forwarded to response UI.
 * @returns True after the request response has been handled.
 */
async function cardsOpenRequestShareFromEntry(
  entryData: CardsShareEntry,
  origin: PopupDialogOrigin | undefined,
): Promise<boolean> {
  const state = user();
  const currentTime = now();
  const isNewUser = isFirstEntry();
  const ownedCardCount = cardsGetOwnedCardCount(
    state,
    entryData.cardId,
    currentTime,
  );

  if (!isNewUser && !cardsIsAvailable(state)) {
    await cardsOpenShareResponsePopup(entryData, {
      message: i18n('cards.responsePopup.messageCardsLocked', {
        level: cardsUnlockLevel,
      }),
      origin,
    });
    return true;
  }

  if (!isNewUser && ownedCardCount < 2) {
    await cardsOpenShareResponsePopup(entryData, {
      message: i18n('cards.responsePopup.messageRequestMissingCard'),
      origin,
      count: ownedCardCount,
    });
    return true;
  }

  if (isNewUser && !cardsCanReceiveCard(state, currentTime)) {
    await cardsOpenShareResponsePopup(entryData, {
      message: i18n('cards.responsePopup.messageRequestReceivedLimit'),
      origin,
    });
    return true;
  }

  if (!cardsCanSendCard(state, currentTime)) {
    await cardsOpenShareResponsePopup(entryData, {
      message: i18n('cards.responsePopup.messageRequestSentLimit'),
      origin,
      count: Math.max(1, ownedCardCount),
    });
    return true;
  }

  // Preview whether the request can use redemption or a normal owned-card send.
  const requestPreview = await withLoading(
    () =>
      StateObserver.invoke.cardsPreviewInviteRequest({
        inviterId: entryData.senderId,
        cardId: entryData.cardId,
      }),
    { feature: Feature.CARDS._, subFeature: Feature.CARDS.REQUEST },
  );

  // The redemption route shows the card pair that will be minted before sending.
  if (requestPreview.canSend && requestPreview.redeemCardId) {
    await openPopupPromise('popupCardResponse', {
      cardState: {
        id: requestPreview.redeemCardId,
        count: cardsInviteRequestGrantedCardCount,
        new: false,
      },
      targetId: entryData.senderId,
      inviteRequest: { requestedCardId: entryData.cardId },
      requestMethod: entryData.requestMethod,
      message: i18n('cards.responsePopup.messageInviteWelcome'),
      actionMode: 'send',
      origin,
    });
    return true;
  }

  // The normal route sends one owned duplicate without redeeming the request.
  if (requestPreview.canSend) {
    await openPopupPromise('popupCardResponse', {
      cardState: {
        id: entryData.cardId,
        count: ownedCardCount,
        new: false,
      },
      targetId: entryData.senderId,
      requestMethod: entryData.requestMethod,
      message: i18n('cards.responsePopup.messageRequestSendDuplicate'),
      actionMode: 'send',
      origin,
    });
    return true;
  }

  // Handle every terminal unavailable result within the exhaustive switch.
  const unavailableReason = requestPreview.reason;
  switch (unavailableReason) {
    case 'not_enough_cards': {
      await cardsOpenShareResponsePopup(entryData, {
        message: i18n('cards.responsePopup.messageRequestMissingCard'),
        origin,
        count: ownedCardCount,
      });
      return true;
    }

    case 'send_limit_reached': {
      await cardsOpenShareResponsePopup(entryData, {
        message: i18n('cards.responsePopup.messageRequestSentLimit'),
        origin,
        count: Math.max(1, ownedCardCount),
      });
      return true;
    }

    case 'requester_receive_limit_reached':
      await cardsOpenShareResponsePopup(entryData, {
        message: i18n(
          'cards.responsePopup.messageRequestRequesterReceivedLimit',
        ),
        origin,
        count: Math.max(1, ownedCardCount),
      });
      return true;

    case 'invalid_link':
    case 'friend_unavailable':
    case 'requester_unavailable':
    case 'request_unavailable':
      await cardsOpenShareResponsePopup(entryData, {
        message: i18n('cards.responsePopup.messageRequestUnavailable'),
        origin,
      });
      return true;

    default:
      assertNever(unavailableReason);
  }
}

/** Derive the recipient state used by card-link landing analytics. */
function cardsGetLandingState(entryData: CardsShareEntry): CardsLandingState {
  // First-entry players use the dedicated invite landing state.
  if (isFirstEntry()) return 'new_user';

  // Existing players below the Cards gate use the locked landing state.
  const state = user();
  if (!cardsIsAvailable(state)) return 'below_gate';

  // Distinguish links for cards already owned or pending from missing cards.
  if (cardsHasCardOrPendingReceived(state, entryData.cardId, now())) {
    return 'has_card';
  }

  return 'missing_card';
}

/**
 * Claim a donated card when the receiver and sender remain eligible.
 *
 * @param entryData Validated donate-share entry.
 * @param origin Popup origin forwarded to informational responses.
 * @returns True after the donation link has been handled.
 */
async function cardsOpenDonateShareFromEntry(
  entryData: CardsShareEntry,
  origin: PopupDialogOrigin | undefined,
): Promise<boolean> {
  const state = user();
  const currentTime = now();

  // New users may receive a donated card before the normal Cards level gate.
  if (state.loginCount !== 1 && !cardsIsAvailable(state)) {
    await cardsOpenShareResponsePopup(entryData, {
      message: i18n('cards.responsePopup.messageCardsLocked', {
        level: cardsUnlockLevel,
      }),
      origin,
    });
    return true;
  }

  // Receivers cannot claim cards they already own or are about to receive.
  if (cardsHasCardOrPendingReceived(state, entryData.cardId, currentTime)) {
    await cardsOpenShareResponsePopup(entryData, {
      message: i18n('cards.responsePopup.messageAlreadyHaveCard'),
      origin,
    });
    return true;
  }

  // Receivers must have capacity before asking the server to claim the card.
  if (!cardsCanReceiveCard(state, currentTime)) {
    await cardsOpenShareResponsePopup(entryData, {
      message: i18n('cards.responsePopup.messageDonateReceivedLimit'),
      origin,
    });
    return true;
  }

  // Revalidate sender and receiver eligibility while claiming the donation.
  const result = await withLoading(
    () =>
      StateObserver.invoke.cardsClaimDonateShare({
        senderId: entryData.senderId,
        cardId: entryData.cardId,
      }),
    { feature: Feature.CARDS._, subFeature: Feature.CARDS.DONATE },
  );

  // Server rejections use reason-specific copy without changing local state.
  if (!result.granted) {
    const messageKey =
      DONATE_SHARE_FAILURE_MESSAGE_KEYS[result.reason] ??
      'cards.responsePopup.messageDonateUnavailable';
    await cardsOpenShareResponsePopup(entryData, {
      message: i18n(messageKey),
      origin,
    });
    return true;
  }

  // Successful claims continue through the standard received-card sequence.
  await cardsCollectReceived();
  return true;
}

/**
 * Prompt the receiver for a counter card and create a trade from a share link.
 *
 * @param entryData Validated trade-share entry.
 * @param origin Popup origin forwarded to informational responses.
 * @returns True after the trade share has been handled.
 */
async function cardsOpenTradeShareFromEntry(
  entryData: CardsShareEntry,
  origin: PopupDialogOrigin | undefined,
): Promise<boolean> {
  // Trade responses require the Cards feature to be unlocked for the receiver.
  if (!cardsIsAvailable(user())) {
    await cardsOpenShareResponsePopup(entryData, {
      message: i18n('cards.responsePopup.messageCardsLocked', {
        level: cardsUnlockLevel,
      }),
      origin,
    });
    return true;
  }

  // The receiver must have capacity to start another trade today.
  if (!cardsCanStartTrade(user(), now())) {
    await cardsOpenShareResponsePopup(entryData, {
      message: i18n('cards.responsePopup.messageTradeDailyLimit'),
      origin,
    });
    return true;
  }

  // The receiver needs at least one duplicate card to offer as a counter.
  const numTradeableCards = Object.keys(
    cardsGetTradeableCards(user(), now()),
  ).length;
  if (numTradeableCards <= 0) {
    await cardsOpenShareResponsePopup(entryData, {
      message: i18n('cards.responsePopup.messageTradeNoCards'),
      origin,
    });
    return true;
  }

  // Confirm that the sender's offer and both players' limits remain valid.
  const preview = await withLoading(
    () =>
      StateObserver.invoke.cardsPreviewTradeShareLink({
        senderId: entryData.senderId,
        offeredCardId: entryData.cardId,
        tradeId: entryData.tradeId,
      }),
    { feature: Feature.CARDS._, subFeature: Feature.CARDS.TRADE },
  );

  // Preview failures end the flow before prompting the receiver.
  if (!preview.granted) {
    const messageKey =
      TRADE_SHARE_FAILURE_MESSAGE_KEYS[preview.reason] ??
      'cards.responsePopup.messageTradeUnavailable';
    await cardsOpenShareResponsePopup(entryData, {
      message: i18n(messageKey),
      origin,
    });
    return true;
  }

  // Show whether the offered card would be new before requesting acceptance.
  const ownedCardCount = cardsGetOwnedCardCount(
    user(),
    entryData.cardId,
    now(),
  );
  const accepted = await openPopupPromise('popupCardTradeShareInvite', {
    cardState: {
      id: entryData.cardId,
      count: 1,
      new: ownedCardCount <= 0,
    },
  });
  if (!accepted) {
    return true;
  }

  // Let the receiver select the duplicate card offered in return.
  const counterCard = await cardsPickCardToTrade();
  if (!counterCard) {
    return true;
  }

  // Revalidate the offer while creating the pending trade on the server.
  const result = await withLoading(
    () =>
      StateObserver.invoke.cardsCreateTradeFromShareLink({
        senderId: entryData.senderId,
        offeredCardId: entryData.cardId,
        counterCardId: counterCard.id,
        tradeId: entryData.tradeId,
      }),
    { feature: Feature.CARDS._, subFeature: Feature.CARDS.TRADE },
  );

  // Creation failures explain why the trade could not be submitted.
  if (!result.granted) {
    const messageKey =
      TRADE_SHARE_FAILURE_MESSAGE_KEYS[result.reason] ??
      'cards.responsePopup.messageTradeUnavailable';
    await cardsOpenShareResponsePopup(entryData, {
      message: i18n(messageKey),
      origin,
    });
    return true;
  }

  // Successful creation confirms that the countered trade was sent.
  await cardsOpenShareResponsePopup(entryData, {
    message: i18n('cards.responsePopup.messageTradeSent'),
    origin,
  });
  return true;
}
